import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { CURRENCY_TO_COUNTRY } from '../../constants';

interface CurrentRatesTableProps {
    fromCurrency: string;
    selectedCurrencies: string[];
    exchangeRates: { [key: string]: number };
    isRefreshing: boolean;
    onRefresh: () => void;
    onRemoveCurrency: (currency: string) => void;
}

const CurrentRatesTable = ({
    fromCurrency,
    selectedCurrencies,
    exchangeRates,
    isRefreshing,
    onRefresh,
    onRemoveCurrency,
}: CurrentRatesTableProps) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'fa'; // Farsi is RTL
    
    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">
                    {t('rates.currentRates')} ({fromCurrency})
                </Typography>
                <Tooltip title={t('common.refresh')}>
                    <IconButton onClick={onRefresh} disabled={isRefreshing}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedCurrencies.map((currency) => {
                    const info = CURRENCY_TO_COUNTRY[currency];
                    return (
                        <Chip
                            key={currency}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <span>{info.flag}</span>
                                    <span>{currency}</span>
                                </Box>
                            }
                            onDelete={() => onRemoveCurrency(currency)}
                            color="primary"
                            variant="outlined"
                            sx={isRTL ? {
                                '& .MuiChip-deleteIcon': {
                                    order: -1, // Move delete icon to the left for RTL
                                    marginLeft: '4px',
                                    marginRight: '0px'
                                },
                                '& .MuiChip-label': {
                                    order: 1, // Move label to the right for RTL
                                    paddingLeft: '8px',
                                    paddingRight: '4px'
                                }
                            } : {}}
                        />
                    );
                })}
            </Box>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('common.currency')}</TableCell>
                            <TableCell>{t('rates.targetCurrency')}</TableCell>
                            <TableCell align="right">{t('rates.rate')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {selectedCurrencies.map((currency) => {
                            const info = CURRENCY_TO_COUNTRY[currency];
                            const isIRR = currency === 'IRR';

                            // If currency is the same as fromCurrency, rate should be 1
                            const rate = currency === fromCurrency ? 1 : exchangeRates[currency];

                            const displayRate = rate ? (
                                isIRR ? (
                                    Math.round(rate).toLocaleString()
                                ) : (
                                    rate.toFixed(8)
                                )
                            ) : t('common.loading');

                            return (
                                <TableRow key={currency}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>{info.flag}</span>
                                            <span>{currency}</span>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{info.name}</TableCell>
                                    <TableCell align="right">
                                        {displayRate}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default CurrentRatesTable; 